import { Box } from "@mui/system";
import FilterPill from "../Common/FilterPill";
import styles from "../Meetings/Meetings.Style";
import { useLocation } from "react-router-dom";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import SlideOver from "../Common/SlideOver";
import ActionItem from "./ActionItem";
import useGlobalContext from "../../context/useGlobalContext";
import ActionItems from "./ActionItems";
import ActionItemService from "../../services/actionItems/ActionItemService";
import { resolveIsSubTaskFlag } from "../../utils/subtaskUtils";
import Avatar from '@mui/material/Avatar';
import "../../styles/actionItems.css"
import { ActionItemType, DateRangeValue, Options, Tag } from "../../utils/types";
import { capitalizeFirstLetter, getUniqueAccounts, getUniqueAssignees, 
  PRIORITY_OPTIONS, formatDate, getAvatarInitials, 
  getAvatarColor, 
  getUniqueTags,
  formatDateOnly,
  getStatusOptions, getLoggedInUserDomain,
  getCustomDefaultRange,
  normalizeColumnFilters,
  normalizeOverdueRange,
  buildDateRange,
  getParamFromUrl,
  getLastTwoYearsToYesterdayRange,
  normalizeLeadingCapital,
  ACTION_OPTIONS,
  downloadBlob,
  prepareExportDownload,
  parseBlobError,
} from "../../utils/utils";
import { format } from "date-fns";
import { initialSort } from "./actionItemTableConfig";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import GroupByTask from "./GroupByTask";
import UserInteractionsService from "../../services/interactions/Interactions";
import { Tooltip } from "@mui/material";
import ClientService from "../../services/clients/ClientService";
import BulkUpdateBar from "../Common/BulkUpdateBar";
import DefaultFlag from "../../assets/action-items/default-flag.svg";
import StatusIcon from "../../assets/action-items/status-icon.svg";
import AssigneeIcon from "../../assets/action-items/assignee-icon.svg";
import SoloCoachIcon from "../../assets/action-items/coach-icon.svg";
import SubscribeIcon from "../../assets/action-items/subscribe-icon.svg";
import ExecSponser from "../../assets/action-items/sponser-icon.svg";
import AccountIcon from "../../assets/action-items/client-icon.svg";
import DueDateIcon from "../../assets/action-items/due-date-calendar.svg"
import { useQueryClient } from "@tanstack/react-query";
import TagsListIcon from "../../assets/action-items/tag-list.svg";
import ActionItemsTagsCell from "./ActionItemsTagsCell";
import CustomConfirmation from "../Common/CustomConfirmation";
import ColumnFilter from "../Common/customFilter";
import { useSearchParams } from "react-router-dom";
import IntegrationService from "../../services/integrations/Integrations";
import { getDateRangeFromPreference, mapPreferenceColumnsToVisibility, resolveColumnVisibility } from "./TaskManagementLogic";
import DownloadReport from "../Common/DownloadReport";


interface TaskManagementParams {
  setTaskManagementDateRange?: React.Dispatch<
    React.SetStateAction<DateRangeValue>
  >;
  isSupportTicketOpen: boolean;
  setIsSupportTicketOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TASK_MANAGEMENT_DATE_PRESET_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Tomorrow", value: "tomorrow" },
  { label: "Current Week", value: "currentWeek" },
  { label: "Current Month", value: "currentMonth" },
  { label: "Last Week", value: "lastWeek" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Custom Date", value: "custom" },
];

const isPlaceholderActionItemId = (id?: string | null) =>
  typeof id === "string" && (id === "__add_task__" || id.startsWith("__add_sub_task_"));

const collectVisibleActionItemIds = (items: any[] = []): string[] =>
  items.flatMap((item) =>
    item?.actionItemId && !isPlaceholderActionItemId(item.actionItemId)
      ? [item.actionItemId]
      : []
  );

const getRealSelectedActionItemIds = (ids: any[] = []) =>
  ids.filter((id) => id && !isPlaceholderActionItemId(String(id)));

const TaskManagement = forwardRef(({
  setTaskManagementDateRange,
  isSupportTicketOpen,
  setIsSupportTicketOpen,
}: TaskManagementParams, ref) => {

  const TASK_PREF_KEY = "TaskPreferences";
  const TASK_DATE_SESSION_KEY = "TaskManagementDueDateRange";
  const TASK_TABLE_SESSION_KEY = "TaskManagementTableState";
  const savePreferences = (data: any) => {
    localStorage.setItem(TASK_PREF_KEY, JSON.stringify(data));
  };
  const getPreferences = () => {
    try {
      return JSON.parse(localStorage.getItem(TASK_PREF_KEY) || "{}");
    } catch {
      return {};
    }
  };
  const isValidDateRange = (range: any): range is DateRangeValue =>
    !!range &&
    typeof range === "object" &&
    typeof range.startDate === "string" &&
    typeof range.endDate === "string";
  const saveSessionDateRange = (range: DateRangeValue) => {
    sessionStorage.setItem(TASK_DATE_SESSION_KEY, JSON.stringify(range));
  };
  const getSessionDateRange = (): DateRangeValue | null => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(TASK_DATE_SESSION_KEY) || "null");
      return isValidDateRange(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const saveTableSession = (data: any) => {
    const existing = JSON.parse(
      sessionStorage.getItem(TASK_TABLE_SESSION_KEY) || "{}"
    );

    sessionStorage.setItem(
      TASK_TABLE_SESSION_KEY,
      JSON.stringify({
        ...existing,
        ...data,
      })
    );
  };

  const getTableSession = () => {
    try {
      return JSON.parse(
        sessionStorage.getItem(TASK_TABLE_SESSION_KEY) || "{}"
      );
    } catch {
      return {};
    }
  };

  const urlFilters = getParamFromUrl();

  const location = useLocation();
  const { showSnackbar } = useDisplaySnackbar();
  const { startEndMeetingTime, accountDetails, featureMap } = useGlobalContext();
  const incomingFilterState = location.state?.filterState;
  const hasIncomingFilterDateRange = !!incomingFilterState?.taskManagementFilterDateRange;
  const hasAppliedPreferenceTimePeriodRef = useRef(false);
  const previousNonOverdueDateRangeRef = useRef<DateRangeValue | null>(null);
  const [tastManagementFilterDateRange, setTaskManagementFilterDateRange] = useState<DateRangeValue>(() => {
    if (urlFilters?.dueDate) {
      return buildDateRange(urlFilters.dueDate);
    }
    if (incomingFilterState?.taskManagementFilterDateRange) {
      return incomingFilterState.taskManagementFilterDateRange;
    }
    return getSessionDateRange() || getCustomDefaultRange();
  });
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [selectedActionItem, setSelectedActionItem] = useState<any | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isNewActionItem, setIsNewActionItem] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [discardTrigger, setDiscardTrigger] = useState(false);
  const handleSave = () => setSaveTrigger(true);
  const [isActionItemValid, setIsActionItemValid] = useState(false);
  const getTabFromPath = (pathname: string): string => {
    if (pathname.includes("/tasks/all-tasks")) return "allTasks";
    if (pathname.includes("/tasks/my-tasks")) return "myTasks";
    if (pathname.includes("/tasks/team-tasks")) return "teamTasks";
    if (pathname.includes("/tasks/subscribed-tasks")) return "subscribedTasks";
    if (pathname.includes("/tasks/complete-mom-tasks")) return "completeMomTasks";
    if (pathname.includes("tasks/ai-recommended-tasks")) return "aiRecommendedTasks"
    return "allTasks"; // default
  };
  const selectedTab = getTabFromPath(location.pathname);
  const getDefaultSortForTab = (tab: string) => (
    tab === "aiRecommendedTasks"
      ? { sort_field: "createdAt", sort_order: "desc" as "asc" | "desc" }
      : initialSort
  );
  const isExternalUser = accountDetails?.isExternalUser
  const [searchParams] = useSearchParams();
  const [attendees, setAttendees] = useState<any>([]);
  const [actionItemType, setActionItemType] = useState(ActionItemType.All);
  const loggedInProfileOrgId = accountDetails?.profileOrgId;
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<{ sort_field: string; sort_order: "asc" | "desc" }>(() => {
    if (!!urlFilters) return getDefaultSortForTab(selectedTab);
    const session = getTableSession();
    return ( session.sort || getDefaultSortForTab(selectedTab));
  });
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [actionItems, setActionItems] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [groupBy, setGroupBy] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<{ type: string; value: string | null }>({
    type: "",
    value: null,
  });
  const [selectedActionItemIds, setSelectedActionItemIds] = useState<any[]>([]);
  const [bulkUpdateValues, setBulkUpdateValues] = useState<Record<string, any>>({});
  const [filterOrder, setFilterOrder] = useState<string[]>([]);
  const [groupByValues, setGroupByValues] = useState<{
    groupByInteractionId: string | null;
    groupByClientDomain: { clientName: string | null; orgId: string | null } | null;
    groupByAssigneeId: string | null;
    groupByStatus: string | null;
  }>({
    groupByInteractionId: null,
    groupByClientDomain: null,
    groupByAssigneeId: null,
    groupByStatus: null,
  });
  const groupByRef = useRef<any>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const expandedGroupIndexRef = useRef<number | null>(null);
  const STATUS_OPTIONS = getStatusOptions();
  const loggedInUserDomain = getLoggedInUserDomain();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesConfirm, setShowUnsavedChangesConfirm] = useState(false);
  const [isUnassignedGroupExpanded, setIsUnassignedGroupExpanded] = useState(false);
  const [isNonRecurringInteractionTasks, setIsNonRecurringInteractionTasks] = useState(false);
  const exportAIRecommendedTasksDataEnabled = featureMap?.export_ai_recommended_tasks_data === true;
  const getDefaultColumnFilters = () => ({
    assignee: [],
    coach: [],
    dueDate: "",
    priority: [],
    account: [],
    actionItem: "",
    status: [
      ...(loggedInUserDomain === "anarix.io"
        ? [
            "open",
            "in_progress",
            "devbox",
            "parent_branch",
            "pr_review",
            "dev_testing",
            "dev_approved",
            "test_approved",
            "prod_testing",
          ]
        : loggedInUserDomain === "7thgear.ai"
        ? [
            "to_do",
            "in_progress",
            "pr_review",
            "product_testing",
          ]
        : [
            "to_do",
            "in_progress",
          ]),
    ],
    description: "",
    tags: [],
    sponsor: [],
    subscribers: [],
    actions: [],
  });
  const [columnFilters, setColumnFilters] = useState(() => {
    const base = getDefaultColumnFilters();

  if (urlFilters) {
    return {
      ...base,
      ...(urlFilters?.status && { status: urlFilters.status }),
      ...(urlFilters?.overdue && { dueDate: ["overdue"] }),
    };
  }

    const session = getTableSession();
    const restored = session.columnFilters || base;
    if (restored && restored.actions && !Array.isArray(restored.actions)) {
      restored.actions = [];
    }
    return restored;
  });

  const isOverdueSelected =
    Array.isArray(columnFilters?.dueDate) &&
    columnFilters?.dueDate.includes("overdue");

  const { startDate: apiStartDate, endDate: apiEndDate } =
    normalizeOverdueRange(
      tastManagementFilterDateRange?.startDate,
      tastManagementFilterDateRange?.endDate,
      isOverdueSelected
    );

  const [groupByList, setGroupByList] = useState<{
    interactions: any[];
    clients: any[];
    assignees: any[];
    recurringInteractions: any[];
    statuses: any[];
  }>({
    interactions: [],
    clients: [],
    assignees: [],
    recurringInteractions: [],
    statuses: [],
  });
  const [customConfirm, setCustomConfirm] = useState<boolean>(false)

  const [clientList, setClientList] = useState<any[]>([]);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [spacesList, setSpacesList] = useState<any[]>([]);

  const [isNotClientTasks, setIsNotClientTasks] = useState(false);
  const [groupExpandToken, setGroupExpandToken] = useState(0);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const session = getTableSession();

    const visibilityKey =
      selectedTab === "aiRecommendedTasks"
        ? "aiRecommendedTasks"
        : "default";

    return resolveColumnVisibility(
      selectedTab,
      session.columnVisibility?.[visibilityKey]
    );
  });
  const tableColumnVisibility = {
    ...columnVisibility,
    actions:
      selectedTab === "aiRecommendedTasks"
        ? columnVisibility.actions !== false
        : false,
  };
  const columnVisibilityTabRef = useRef(selectedTab);

  const handleSelect = (label: string, value: any) => {
    setBulkUpdateValues((prev) => ({
      ...prev,
      [label]: value,
    }));
  };
  const usersList = activeUsers.map((user) => ({
    label:capitalizeFirstLetter(user.name),
    value: user?.profileOrgRelationshipId,
    pictureUrl: user?.pictureUrl,
    type: "user" as const,
    email: user?.email,
  }));
  const accountList = React.useMemo(() => {
    return clientList.map((client) => ({
      label: capitalizeFirstLetter(client.clientName),
      value: client.clientOrgId,
    }));
  }, [clientList]);
  const [tagsList, setTagsList] = useState<any[]>([]);
  const formattedTagsList: Tag[] = tagsList.map(t => ({
    tagName: t.tagName,
    tagId: t.tagId,
  }));
  
  const spacesListFormatted = spacesList.map((s) => ({
    label: s.spaceName,
    value: s.spaceId,
    type: "space" as const, // literal type
  }));

  useEffect(() => {
    const groupByParam = urlFilters?.groupBy ;

    if (groupByParam) {
      const freshPrefs: any = {};
      if (groupByParam) {
        freshPrefs.groupBy = groupByParam;
      } else {
        freshPrefs.groupBy = "";
      }
      freshPrefs.sort = getDefaultSortForTab(selectedTab);
      savePreferences(freshPrefs);
    }
  }, [searchParams, selectedTab]);

  useEffect(() => {
    if (!tastManagementFilterDateRange?.overdue) {
      previousNonOverdueDateRangeRef.current = {
        ...tastManagementFilterDateRange,
        overdue: false,
      };
    }
  }, [tastManagementFilterDateRange]);

  useEffect(() => {
    if (setTaskManagementDateRange) {
      setTaskManagementDateRange(tastManagementFilterDateRange);
    }
     // Reset group-by on date change
    setSelectedGroup({ type: "", value: null });
    setIsNotClientTasks(false);
    setIsNonRecurringInteractionTasks(false);
    setGroupExpandToken(0);

    setGroupByValues({
      groupByInteractionId: null,
      groupByClientDomain: { clientName: null, orgId: null },
      groupByAssigneeId: null,
      groupByStatus: null,
    });
  }, [tastManagementFilterDateRange, setTaskManagementDateRange]);

  useEffect(() => {
    if (isOverdueSelected) {
      const range = getLastTwoYearsToYesterdayRange();
      const overdueRange = {
        preset: "custom",
        startDate: range.start_time,
        endDate: range.end_time,
        overdue: true,
      };

      saveSessionDateRange(overdueRange);
      setTaskManagementFilterDateRange(overdueRange);
    } else {
      setTaskManagementFilterDateRange((prev) => {
        if (!prev?.overdue) return prev;
        const restoredRange = (
          previousNonOverdueDateRangeRef.current ||
          incomingFilterState?.taskManagementFilterDateRange ||
          getCustomDefaultRange()
        );
        saveSessionDateRange(restoredRange);
        return restoredRange;
      });
    }
  }, [isOverdueSelected]);  
  useEffect(() => {
    if (columnFilters.status?.length) {
      updateColumnFilter("status", columnFilters.status);
    }
  }, [columnFilters.status]);

  const isNoAccountSelected = columnFilters?.account?.includes("no_account");

  useEffect(()=>{
      const saved = getPreferences();
      const session = getTableSession();
      setSelectedActionItemIds([]);
      setBulkUpdateValues({});
      setGroupByValues({groupByInteractionId: null, groupByClientDomain: { clientName: null, orgId: null }, groupByAssigneeId: null, groupByStatus: null,});
      if(saved.groupBy) {
        setGroupBy(saved.groupBy);
      } else {
        setGroupBy("");
      }
      if (selectedTab === "aiRecommendedTasks") {
        setSort({
          sort_field: "createdAt",
          sort_order: "desc",
        });
      } else {
        setSort(
          session.sort || getDefaultSortForTab(selectedTab)
        );
      }
      setPageNumber(1);
      setPageSize(50);
      setActionItems([]); // Clear old data when tab changes
      setTotalRecord(0); // Reset total record count
      setSelectedGroup({ type: "", value: null });
      setIsNotClientTasks(false);
      setIsNonRecurringInteractionTasks(false);
      setGroupExpandToken(0);
      setGroupByValues({
        groupByInteractionId: null,
        groupByClientDomain: { clientName: null, orgId: null },
        groupByAssigneeId: null,
        groupByStatus: null,
      });
      setColumnFilters((prev: any) => {
        if (selectedTab !== "aiRecommendedTasks" && prev?.actions?.length > 0) {
          const nextFilters = {
            ...prev,
            actions: [],
          };
          saveTableSession({
            columnFilters: nextFilters,
          });
          return nextFilters;
        }
        return prev;
      });
      const sessionForVisibility = getTableSession();
      const visibilityKey =
        selectedTab === "aiRecommendedTasks"
          ? "aiRecommendedTasks"
          : "default";
      setColumnVisibility(
        resolveColumnVisibility(
          selectedTab,
          sessionForVisibility.columnVisibility?.[visibilityKey]
        )
      );
  },[selectedTab])
  type ColumnFilterKeys = keyof typeof columnFilters & string;
  useEffect(() => {
    setSelectedGroup({ type: "", value: null });
    setIsNotClientTasks(false);
    setIsNonRecurringInteractionTasks(false);
    setGroupExpandToken(0);
    setGroupByValues({
      groupByInteractionId: null,
      groupByClientDomain: { clientName: null, orgId: null },
      groupByAssigneeId: null,
      groupByStatus: null,
    });
    // When columnFilters changes, update order if new keys appear
    (Object.keys(columnFilters) as ColumnFilterKeys[]).forEach((key) => {
      const value = columnFilters[key];
      if (key === "description") {
        if (columnFilters.description && !filterOrder.includes("description")) {
          setFilterOrder((prev) => [...prev, "description"]);
        }
        if (!columnFilters.description && filterOrder.includes("description")) {
          setFilterOrder((prev) => prev.filter((k) => k !== "description"));
        }
        return;
      }
      if (value && (!Array.isArray(value) || value.length > 0)) {
        if (!filterOrder.includes(key)) {
          setFilterOrder((prev) => [...prev, key]);
        }
      } else {
        if (filterOrder.includes(key)) {
          setFilterOrder((prev) => prev.filter((k) => k !== key));
        }
      }
    });
  }, [columnFilters]);
  const isValidDate = (date: any): boolean => {
    if (!date) return false;
    const parsed = typeof date === "string" ? new Date(date) : date;
    return parsed instanceof Date && !isNaN(parsed.getTime());
  };
  const validEmployeeProfileId = loggedInProfileOrgId;
  const {
    isLoading: isUserPreferencesLoading,
    isFetched: isUserPreferencesFetching,
    isSuccess: isUserPreferencesSuccess,
    isError: isUserPreferencesError,
    error: userPreferencesError,
    data: userPreferencesData,
  } = IntegrationService.useGetUserPreferences(
    { employeePorId: validEmployeeProfileId ?? "" },
    !!validEmployeeProfileId && selectedTab !== "aiRecommendedTasks"
  );

  useEffect(() => {
    if (isUserPreferencesSuccess && userPreferencesData) {
      const session = getTableSession();

      const visibilityKey =
        selectedTab === "aiRecommendedTasks"
          ? "aiRecommendedTasks"
          : "default";

      const savedVisibility =
        session.columnVisibility?.[visibilityKey];

      if (savedVisibility) {
        setColumnVisibility(resolveColumnVisibility(selectedTab, savedVisibility));
        return;
      }

      const taskColumns =
        userPreferencesData?.userPreferences?.table_preferences?.tasks?.columns;

      setColumnVisibility(
        resolveColumnVisibility(
          selectedTab,
          mapPreferenceColumnsToVisibility(taskColumns, selectedTab)
        )
      );
    }
  }, [isUserPreferencesError, userPreferencesError, userPreferencesData, selectedTab, isUserPreferencesSuccess]);

  useEffect(() => {
    if (hasAppliedPreferenceTimePeriodRef.current) return;
    if (urlFilters?.dueDate || hasIncomingFilterDateRange) {
      hasAppliedPreferenceTimePeriodRef.current = true;
      return;
    }
    if (selectedTab === "aiRecommendedTasks") {
      hasAppliedPreferenceTimePeriodRef.current = true;
      return;
    }
    const sessionDateRange = getSessionDateRange();
    if (sessionDateRange) {
      if (!sessionDateRange.overdue) {
        previousNonOverdueDateRangeRef.current = {
          ...sessionDateRange,
          overdue: false,
        };
      }
      setTaskManagementFilterDateRange(sessionDateRange);
      setColumnFilters((prev: any) => ({
        ...prev,
        dueDate: sessionDateRange.overdue ? ["overdue"] : [],
      }));
      hasAppliedPreferenceTimePeriodRef.current = true;
      return;
    }
    if (!validEmployeeProfileId) return;
    if (!isUserPreferencesSuccess && !isUserPreferencesError) return;

    const defaultTimePeriod =
      userPreferencesData?.userPreferences?.table_preferences?.tasks?.default_time_period ?? null;
    const nextRange = getDateRangeFromPreference(defaultTimePeriod);

    previousNonOverdueDateRangeRef.current = {
      ...nextRange,
      overdue: false,
    };
    setTaskManagementFilterDateRange(nextRange);
    setColumnFilters((prev: any) => ({
      ...prev,
      dueDate: nextRange.overdue ? ["overdue"] : [],
    }));
    hasAppliedPreferenceTimePeriodRef.current = true;
  }, [
    userPreferencesData,
    hasIncomingFilterDateRange,
    isUserPreferencesError,
    isUserPreferencesSuccess,
    urlFilters?.dueDate,
    selectedTab,
    validEmployeeProfileId,
  ]);

  const shouldCallApi = !!(
    validEmployeeProfileId &&
    ((isValidDate(tastManagementFilterDateRange?.startDate) &&
      isValidDate(tastManagementFilterDateRange?.endDate)) ||
      isExternalUser)
  );

  const { mutate: mutateActionItemUpdate } = ActionItemService.useUpdateActionItem(
    getRealSelectedActionItemIds(selectedActionItemIds),
    {
      onMutate: () => {
        setShowSpinner?.(true);
        sessionStorage.setItem("actionItemUpdated", JSON.stringify(true));
      },
      onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
        showSnackbar("success", "Task updated successfully.");
        setSelectedActionItemIds([]);          // clear selection
        setBulkUpdateValues({});               // clear update fields
        if(groupBy){
          queryClient.invalidateQueries({
            queryKey: ["get_group_by_action_items"],
          });
        }
        queryClient.invalidateQueries({
          queryKey: ["get_action_item_count"],
        });
      },
      onError: (error: any) => {
        showSnackbar(
          "error",
          error?.response?.data?.message || "Failed to update task."
        );
      },
      onSettled: () => {
        setShowSpinner?.(false);
      },
    }
  );

  const { mutate: mutateActionItemDelete } =
    ActionItemService.useDeleteActionItem(
      selectedActionItemIds,
      {
        onMutate: () => {
          setShowSpinner?.(true);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["get_my_action_items"],
            });
            if(groupBy){
              queryClient.invalidateQueries({
                queryKey: ["get_group_by_action_items"],
              });
            }
            queryClient.invalidateQueries({
              queryKey: ["get_action_item_count"],
            });
          showSnackbar("success", "Task updated successfully.");
          setCustomConfirm(false);
          setSelectedActionItemIds([]);          // clear selection
          setBulkUpdateValues({});               // clear update fields
        },
        onError: (error: any) => {
          showSnackbar("error", error?.response?.data?.message || "Failed to update task.");
        },
        onSettled: () => {
          handleClose();
          setShowSpinner?.(false);
        },
      }
    );
    const toStringArray = (value: any): string[] | undefined => {
      if (value === undefined || value === null || value === "") return undefined;
      return Array.isArray(value) ? value : [value];
    };
    const subscriberPorIds =
      columnFilters.subscribers
        ?.filter((s: any) => s?.entityType === "user")
        ?.map((s: any) => s?.id);

    const subscriberUserGroupIds =
      columnFilters.subscribers
        ?.filter((s: any) => s?.entityType === "space")
        ?.map((s: any) => s?.id);

  const getSharedTaskFilterParams = () => ({
      employeePorId: validEmployeeProfileId,
      startTime: selectedTab !== "aiRecommendedTasks" ? formatDateOnly(apiStartDate || "") : undefined,
      endTime: selectedTab !== "aiRecommendedTasks" ? formatDateOnly(apiEndDate || "") : undefined,
      status: selectedGroup.type === "status" ? selectedGroup.value : columnFilters.status.length ? columnFilters.status.join(",") : "",
      actionItem: columnFilters.description,
      clientOrgIdsForFilters: columnFilters?.account?.filter((id: string) => id !== "no_account")?.length
        ? columnFilters.account.filter((id: string) => id !== "no_account")
        : [],
      assignorPorIds: [] as string[],
      isForTeam: selectedTab === "teamTasks",
      assigneePorIds: toStringArray(
        selectedGroup.type === "assignee"
          ? selectedGroup.value
          : columnFilters.assignee.length
          ? columnFilters.assignee
          : undefined
      ),
      priority: columnFilters?.priority?.length ? columnFilters?.priority.join(",") : "",
      isTaskOverview: selectedTab === "allTasks" || selectedTab === "completeMomTasks" ? true : false,
      isTrackingActionItems: selectedTab === "subscribedTasks" ? true : false,
      coachPorIds: columnFilters?.coach?.length ? columnFilters?.coach : [],
      interactionId: isNonRecurringInteractionTasks
        ? ""
        : selectedGroup.type === "interaction" || selectedGroup.type === "recurring_interaction"
          ? selectedGroup.value || ""
          : "",
      clientOrgId: selectedGroup.type === "client" ? selectedGroup.value || "" : "",
      tagIds: columnFilters?.tags?.length ? columnFilters.tags : [],
      sponsorPorIds: columnFilters?.sponsor?.length ? columnFilters?.sponsor : [],
      ...(isNotClientTasks && { isNotClientTasks: true }),
      subscriberUserGroupIds,
      subscriberPorIds,
      ...(isNoAccountSelected && { isNotClientTasks: true }),
      __groupExpandToken: groupExpandToken,
      isCompleteMomTask: selectedTab === "completeMomTasks" ? true : false,
      isAiRecommendedActionItems: selectedTab === "aiRecommendedTasks" ? true : false,
      createdAtStartTime: selectedTab === "aiRecommendedTasks" ? apiStartDate : "",
      createdAtEndTime: selectedTab === "aiRecommendedTasks" ? apiEndDate : "",
      ...(isUnassignedGroupExpanded && { isAiRecommendedUnassignedActionItems: true }),
      ...(isNonRecurringInteractionTasks && { isNonRecurringInteractionTasks: true }),
      aiActionItemsStates: selectedTab === "aiRecommendedTasks" && columnFilters?.actions?.length > 0 ? columnFilters.actions : undefined,
  });

  const {
    isLoading: isMyActionsLoading,
    isFetching: isMyActionsFetching,
    isSuccess: isMyActionsDataSuccess,
    isError: isMyActionsDataError,
    error: myActionsDataError,
    data: myActionsData,
  } = ActionItemService.useGetPageActionItems(
    {
      ...getSharedTaskFilterParams(),
      offset: pageNumber,
      limit: pageSize,
      isOverdue: isOverdueSelected,
      sortOrder: sort.sort_order,
      sortBy: sort.sort_field,
    },
    shouldCallApi
  );

  const {
    isLoading: isActionItemCountLoading,
    isFetching: isActionItemCountFetching,
    isSuccess: isActionItemCountSuccess,
    isError: isActionItemCountError,
    error: actionItemCountError,
    data: actionItemCountData,
    refetch: refetchActionItemCount,
  } = ActionItemService.useGetActionItemCount(
    {
      ...getSharedTaskFilterParams(),
      isOverdue: isOverdueSelected,
    },
    shouldCallApi
  );

  const {
    mutate: exportAiRecommendedTasks,
    isPending: isAiRecommendedTasksExportPending,
  } = ActionItemService.useExportAiRecommendedTasks({
    onSuccess: async (data: Blob) => {
      try {
        const file = await prepareExportDownload(data);
        const start = format(new Date(tastManagementFilterDateRange.startDate), "MMM_dd_yyyy");
        const end = format(new Date(tastManagementFilterDateRange.endDate), "MMM_dd_yyyy");
        downloadBlob(file, `AI_Recommended_Tasks_${start}_to_${end}.csv`);
      } catch (err: any) {
        showSnackbar("error", err.message || "Failed to download report.");
      }
    },
    onError: async (error: any) => {
      const errMsg = await parseBlobError(error);
      showSnackbar("error", errMsg);
    },
  });

  const handleExportAiRecommendedTasks = () => {
    if (!exportAIRecommendedTasksDataEnabled || isAiRecommendedTasksExportPending) return;
    exportAiRecommendedTasks({
      ...getSharedTaskFilterParams(),
      sortOrder: sort.sort_order,
      sortBy: sort.sort_field,
    });
  };

  useEffect(() => {
    if (isActionItemCountSuccess && actionItemCountData) {
      setTotalRecord(actionItemCountData?.count || 0);
    }
    if (isActionItemCountError) {
      showSnackbar("error", actionItemCountError?.message);
    }
  }, [isActionItemCountSuccess, isActionItemCountError, actionItemCountData, actionItemCountError,]);

  useEffect(() => {
    refetchActionItemCount();
  }, [groupBy]);
  useEffect(() => {
    const isGrouped = groupBy && groupBy !== "undefined";
    setPageSize(isGrouped ? 25 : 50);
   // Clear group-specific selections
    setSelectedGroup({ type: "", value: null });
    setIsNotClientTasks(false);
    setIsNonRecurringInteractionTasks(false);
    setGroupExpandToken(0);
    setGroupByValues({
      groupByInteractionId: null,
      groupByClientDomain: { clientName: null, orgId: null },
      groupByAssigneeId: null,
      groupByStatus: null,
    });
     // Clear old list + prepare for fresh fetch
    setActionItems([]);
    setShowSpinner(true);
    setSelectedActionItemIds([])
    
     // When leaving grouped view → reset pagination
  if (!isGrouped) {
    setPageNumber(1);
  }
    savePreferences({
      ...getPreferences(),
      groupBy
    });
  }, [groupBy]);

  useEffect(() => {
    saveTableSession({
      sort,
    });
  }, [sort]);

  useEffect(() => {
    const isGrouped = groupBy && groupBy !== "undefined";
    if(isGrouped && selectedTab){
      setPageSize(isGrouped ? 25 : 50);
    }
  }, [groupBy, selectedTab]);

  useEffect(() => {
    // If API is not enabled, stop the spinner immediately
    if (!shouldCallApi) {
      setShowSpinner(false);
      return;
    }
    setShowSpinner(
      isMyActionsLoading || 
      isMyActionsFetching
    );
    
    // Only update data when not loading/fetching to prevent showing stale cached data
    if (isMyActionsDataSuccess && !isMyActionsLoading && !isMyActionsFetching) {
    let myActionItemList = myActionsData?.data?.data || [];

    if (isOverdueSelected && myActionItemList) {
      const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));

      myActionItemList = myActionItemList.filter(
        (item:any) => new Date(item.dueDate) <= yesterday && item.status !== "done"
      );
    }
    setActionItems(myActionItemList);
  }
    if (isMyActionsDataError) {
      showSnackbar("error", myActionsDataError?.message);
    }
  }, [
    isMyActionsLoading,
    isMyActionsFetching,
    isMyActionsDataSuccess,
    isMyActionsDataError,
    myActionsData,
    shouldCallApi,
    groupBy,
    isOverdueSelected,
  ]);

  const {
      isLoading: isGroupbyLoading,
      isFetching: isGroupbyFetching,
      isSuccess: isGroupbySuccess,
      isError: isGroupbyError,
      error: groupbyEror,
      data: groupbyData,
  } = ActionItemService.useGetGroupByActionItems({
    startTime: selectedTab !== "aiRecommendedTasks" ? formatDateOnly(apiStartDate || "") : undefined,
    endTime: selectedTab !== "aiRecommendedTasks" ? formatDateOnly(apiEndDate || "") : undefined,
    createdAtStartTime: selectedTab === "aiRecommendedTasks" ? apiStartDate : "",
    createdAtEndTime: selectedTab === "aiRecommendedTasks" ? apiEndDate : "",
    isOverdue: isOverdueSelected,
    groupByActionItem: groupBy,
    employeePorId: loggedInProfileOrgId,
    status: columnFilters.status.length ? columnFilters.status.join(",") : "",
    actionItem: columnFilters.description,
    clientOrgIdsForFilters: isNoAccountSelected
      ? []
      : columnFilters?.account?.length
      ? columnFilters?.account
      : [],
    assigneePorIds: columnFilters.assignee.length
      ? columnFilters.assignee
      : [],
    priority: columnFilters?.priority?.length ? columnFilters?.priority.join(",") : "",
    coachPorIds: columnFilters?.coach?.length ? columnFilters?.coach : [],
    isForTeam: selectedTab === "teamTasks",
    isTaskOverview: selectedTab === "allTasks" || selectedTab === "completeMomTasks" ? true : false,
    isTrackingActionItems: selectedTab === "subscribedTasks" ? true : false,
    tagIds: columnFilters?.tags?.length ? columnFilters.tags : [],
    sponsorPorIds: columnFilters?.sponsor?.length ? columnFilters?.sponsor : [],
    subscriberUserGroupIds,
    subscriberPorIds,
    ...(isNoAccountSelected && { isNotClientTasks: true }),
    isCompleteMomTask: selectedTab === "completeMomTasks" ? true : false,
    isAiRecommendedActionItems: selectedTab === "aiRecommendedTasks" ? true : false,
    aiActionItemsStates: selectedTab === "aiRecommendedTasks" && columnFilters?.actions?.length > 0 ? columnFilters.actions : undefined,
  }, !!groupBy);

  useEffect(()=>{
    if(isGroupbySuccess){
      const groupByList = groupbyData?.data;
      setGroupByList(groupByList);
    } if(isGroupbyError) {
      showSnackbar("error", groupbyEror?.message)
    }
  },[isGroupbyLoading,isGroupbyFetching, groupbyData, isGroupbyError, groupbyEror ])

  const {
    isSuccess,
    isLoading: isActiveUsersLoading,
    isFetching: isActiveUsersFetching,
    isError,
    error,
    data: activeUsersData,
  } = UserInteractionsService.useGetActiveUsers( {}, true );


  useEffect(()=>{
    const visibleIds = new Set(collectVisibleActionItemIds(actionItems));
    setSelectedActionItemIds(prev =>
      getRealSelectedActionItemIds(prev).filter(id => visibleIds.has(id))
    );
  },[actionItems])
  useEffect(() => {
    if(isSuccess) {
      const activeUsers = activeUsersData?.data?.data || [];
      setActiveUsers(activeUsers);
    }
    if (isError) {
      showSnackbar("error", `Error fetching active users: ${error}`);
    }
  }, [isError, error, activeUsersData, isSuccess, isActiveUsersLoading, isActiveUsersFetching]);

  const {
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    isError: isAccountsError,
    error: accountsError,
    data: AccountsData,
  } = ClientService.useGetActiveClients(true);

  useEffect(() => {
      if (AccountsData) {
      const AccountsList = (AccountsData?.data || []).map((c: any) => ({
        ...c,
        clientNameLower: (c.clientName || "").toLowerCase()
      }));
      setClientList(AccountsList);

      } else if (isAccountsError) {
      showSnackbar("error", accountsError?.message || "Failed to load data.");
      }   
  }, [AccountsData, isAccountsError, accountsError, isAccountsLoading, isAccountsFetching]);

  const {
    isLoading: isTagsLoading,
    isFetching: isTagsFetching,
    isError: isTagsError,
    error: tagsError,
    data: TagsData,
  } = ActionItemService.useGetActionItemTags(true);

  useEffect(() => {
    if(TagsData) {
      const TagsList = TagsData?.data || [];
      setTagsList(TagsList);
    } else if (isTagsError) {
      showSnackbar("error", tagsError?.message || "Failed to load data.");
    }
  }, [TagsData, isTagsError, tagsError, isTagsFetching, isTagsLoading]);

  useEffect(() => {
    if (columnVisibilityTabRef.current !== selectedTab) {
      columnVisibilityTabRef.current = selectedTab;
      return;
    }

    const session = getTableSession();
    const visibilityKey =
      selectedTab === "aiRecommendedTasks"
        ? "aiRecommendedTasks"
        : "default";

    saveTableSession({
      columnVisibility: {
        ...(session.columnVisibility || {}),
        [visibilityKey]: resolveColumnVisibility(selectedTab, columnVisibility),
      },
    });
  }, [columnVisibility, selectedTab]);

  const {
    isLoading: isSpacesLoading,
    isSuccess: isSpacesSuccess,
    isError: isSpacesError,
    error: spacesError,
    data: spacesData,
  } = ActionItemService.useGetSpaces(true);

  useEffect(() => {
    if (isSpacesSuccess && spacesData) {
      setSpacesList(spacesData?.data || []);
    }
    if (isSpacesError) {
      showSnackbar("error", `Error fetching spaces: ${spacesError}`);
    }
  }, [isSpacesSuccess, isSpacesError, spacesData, isSpacesLoading]);

  const updateColumnFilter = (columnKey: string, value: any) => {
    let nextFilters = { ...columnFilters };
    const wasOverdueSelected =
      Array.isArray(columnFilters?.dueDate) &&
      columnFilters.dueDate.includes("overdue");

    if (columnKey === "account") {
      const selectedArr = Array.isArray(value) ? value : [value];
      // KEEP ALL selections
      nextFilters.account = selectedArr;
      // Flag depends ONLY on presence of no_account
      nextFilters.isNotClientTasks = selectedArr.includes("no_account");
    } else {
      nextFilters[columnKey] = value;
    }

    nextFilters = normalizeColumnFilters({
      columnKey,
      value,
      nextFilters,
    });
    const isOverdueSelected =
      Array.isArray(nextFilters.dueDate) &&
      nextFilters.dueDate.includes("overdue");

    // ✅ sync overdue flag
    setTaskManagementFilterDateRange((prev) => {
      if (columnKey === "dueDate" && wasOverdueSelected && !isOverdueSelected) {
        return (
          previousNonOverdueDateRangeRef.current ||
          incomingFilterState?.taskManagementFilterDateRange ||
          getCustomDefaultRange()
        );
      }

      return {
        ...prev,
        overdue: isOverdueSelected,
      };
    });
    setColumnFilters(nextFilters);
    saveTableSession({
      columnFilters: nextFilters,
    });
  };
  const onPageChange = (value: number) => {
    if(!groupBy){
      setActionItems([]); // clear current data
    }
    setShowSpinner(true); // show loader instantly
    setPageNumber(value);
  };

  const onFilter = (_value: {
    date: { start_time: string; end_time: string } | undefined;
    status: undefined | string;
  }) => {
    setPageNumber(1);
  };

  const onTypeChange = (value: ActionItemType) => {
    setActionItemType(value);
    setPageNumber(1);
  };

  const handleActionItemSelection = (actionItem: any, _canEdit?: boolean) => {
    const item = {
      ...actionItem,
      isSubTask: resolveIsSubTaskFlag(actionItem),
    };
    setOpenDialog(true);
    setSelectedActionItem(item);
    setAttendees([item?.assignee]);
    setIsNewActionItem(false);
  };

  const handleActionItemPreview = (actionItem: any) => {
    const item = {
      ...actionItem,
      isSubTask: resolveIsSubTaskFlag(actionItem),
    };
    setOpenDialog(true);
    setSelectedActionItem(item);
    setAttendees([item?.assignee]);
    setIsPreview(true);
    setIsNewActionItem(false);
  };

  const handleEditIconClick = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem({
      ...actionItem,
      isSubTask: resolveIsSubTaskFlag(actionItem),
    });
    setAttendees([actionItem?.assignee]);
    setIsPreview(false);
  };

  const isLoadingForTable = isMyActionsLoading || isMyActionsFetching || isUserPreferencesLoading || isUserPreferencesFetching;
  const isGroupbyLoadingState = isGroupbyLoading;

  const isCountLoading = isActionItemCountLoading || isActionItemCountFetching;

  const totalRecords = totalRecord;

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedActionItem(null);
    setIsPreview(false);
    setIsNewActionItem(false);
    setIsSupportTicketOpen(false);
  };

  useEffect(() => {
    const isGrouped = groupBy && groupBy !== "undefined";
  
    if (isGrouped && isMyActionsDataSuccess && !isLoadingForTable) {
  
      // tell accordion to remain open (no-op)
      if (expandedGroupIndexRef.current != null) {
        groupByRef.current?.scrollToExpanded(expandedGroupIndexRef.current);
      }
    }
  }, [pageNumber, isMyActionsDataSuccess, isLoadingForTable, groupBy]);
  useEffect(() => {
    if(selectedActionItemIds.length === 0) {
      setBulkUpdateValues({});
    }
  }, [selectedActionItemIds])
  useImperativeHandle(ref, () => ({
    openAddTask: () => {
      setIsPreview(false);
      setSelectedActionItem(null);
      setOpenDialog(true);
      setIsNewActionItem(true);
    },
    clearGroupByValues: () => {
      setGroupByValues({
        groupByInteractionId: null,
        groupByClientDomain: { clientName: null, orgId: null },
        groupByAssigneeId: null,
        groupByStatus: null,
      });
    },
    scrollMainToTop: () => {
      mainScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }));

  const handleActionItemUpdate = () => {
    const realSelectedIds = getRealSelectedActionItemIds(selectedActionItemIds);
    if (realSelectedIds.length === 0) {
      showSnackbar("warning", "Select at least one task.");
      return;
    }
  
    if (Object.keys(bulkUpdateValues).length === 0) {
      showSnackbar("warning", "Choose at least one field to update.");
      return;
    }
    const subscribeValue = Array.isArray(bulkUpdateValues.subscribe)
      ? bulkUpdateValues.subscribe
      : [];

    const actionSubscribersPorIds = subscribeValue
      .filter((item: any) => item?.type === "user")
      .map((item: any) => item.value);

    const addSpaces = subscribeValue
      .filter((item: any) => item?.type === "space")
      .map((item: any) => item.value);
    const bulkUpdatePayload = {
      ...(bulkUpdateValues.dueDate?.value && {
        dueDate: formatDateOnly(bulkUpdateValues.dueDate.value)
      }),
    ...(bulkUpdateValues.priority?.value && {
      priority: bulkUpdateValues.priority?.value,
    }),
    ...(bulkUpdateValues.status?.value && {
      status: bulkUpdateValues.status?.value,
    }),
    ...(bulkUpdateValues.account?.value && {
      clientOrgId: bulkUpdateValues.account?.value,
    }),
    ...(bulkUpdateValues.soloPlayer?.value && {
      assigneePorId: bulkUpdateValues.soloPlayer?.value,
    }),
    ...(bulkUpdateValues.soloCoach?.value && {
      coachPorId: bulkUpdateValues.soloCoach?.value,
    }),
    ...(bulkUpdateValues.execSponser?.value && {
      sponsorPorId: bulkUpdateValues.execSponser?.value,
    }),
    ...(actionSubscribersPorIds.length > 0 && {
      actionSubscribersPorIds,
    }),
    ...(addSpaces.length > 0 && {
      addSpaces,
    }),
    ...(bulkUpdateValues.tags?.length > 0 && {
      tagIds: bulkUpdateValues.tags.map((t: any) => t.value),
    }),
  }
    mutateActionItemUpdate(bulkUpdatePayload);
  };

  const renderActionItemsBody = () => (
    <ActionItems
      type={
        selectedTab === "teamTasks"
          ? "TEAM_ACTION_ITEMS"
          : selectedTab === "subscribedTasks"
          ? "SUBSCRIBED_ACTION_ITEMS"
          : "MY_ACTION_ITEMS"
      }
      isLoading={isLoadingForTable}
      actionItems={actionItems}
      totalRecord={totalRecords}
      handleActionItemSelection={handleActionItemSelection}
      handleActionItemPreview={handleActionItemPreview}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onFilter={onFilter}
      actionItemType={actionItemType}
      onTypeChange={onTypeChange}
      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
      startEndMeetingTime={startEndMeetingTime}
      selectedTab={selectedTab}
      handleEditIconClick={handleEditIconClick}
      showSpinner={showSpinner}
      setShowSpinner={setShowSpinner}
      columnFilters={columnFilters}
      updateColumnFilter={updateColumnFilter}
      sort={sort}
      onSortChange={(columnKey, direction) =>
        setSort({
          sort_field: columnKey,
          sort_order: direction,
        })
      }
      activeUsers={activeUsers}
      clientList={clientList}
      spacesList={spacesList}
      tagsList={tagsList}
      tableMinHeight="auto"
      setSelectedActionItemIds={setSelectedActionItemIds}
      selectedActionItemIds={selectedActionItemIds}
      bulkUpdateValues={bulkUpdateValues}
      groupByValues={groupByValues}
      showAddTask={selectedTab === "completeMomTasks" || selectedTab === "aiRecommendedTasks" ? false :true}
      maxRowsConfig={{
        maxVisibleRows: 50,
        rowHeight: 50,
        pageSizeOptions: [5, 10, 15, 20, 25]
      }}
      groupBy={groupBy}
      columnVisibility={tableColumnVisibility}
      openTaskDialog={openDialog}
      isCountLoading={isCountLoading}
    />
  );
  
  const generateAccordionItems = () => {
    if (!groupBy || groupBy === "undefined" || !groupByList ) {
      // Return empty array when no grouping or no data
      return [];
    }

    const moveGroupToEnd = (list: any[] = [], isLast: (item: any) => boolean) => {
      const leading = list.filter((item) => !isLast(item));
      const trailing = list.filter((item) => isLast(item));
      return [...leading, ...trailing];
    };

    // Determine which list to use based on groupBy
    const groupList =
      groupBy === "interaction"
        ? groupByList.interactions
        : groupBy === "client"
        ? moveGroupToEnd(groupByList.clients, (item) => item?.name === "Not Client Tasks")
        : groupBy === "assignee"
        ? groupByList.assignees
        : groupBy === "recurring_interaction"
        ? moveGroupToEnd(
            groupByList.recurringInteractions,
            (item) => item?.recurringEventId === "NON_RECURRING"
          )
        : groupBy === "status"
        ? groupByList.statuses
        : [];

    // Map the selected group list to accordion items
    return groupList.map((item) => {
    // compute id first (IMPORTANT)
    let id =
      groupBy === "assignee"
        ? item.email
        : groupBy === "client"
        ? item.orgId
        : groupBy === "interaction"
        ? item.interactionId
        : groupBy === "status"
        ? item.status
        : "";
      let title: React.ReactNode = "";
      let date = "";
      

      if (groupBy === "assignee") {
        title = (
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
            src={item.pictureUrl || undefined}
            alt={item.fullName || item.email || "Unknown Assignee"}
            sx={{
              bgcolor: !item.pictureUrl ? getAvatarColor(item?.email || item?.fullName) : undefined,
              width: 20,
              height: 20,
              fontSize: 12,
            }}
          >
            {!item.pictureUrl && getAvatarInitials(item?.email || item?.fullName)}
          </Avatar>
            <h1 className="accordion_title">
              {item.profileOrgId === null
                ? "Unassigned Tasks"
                : capitalizeFirstLetter(item.fullName) || item.email || "Unknown Assignee"}
            </h1>
            <h3 className="accordion_title accordion_count">{item.totalCount}</h3>
          </Box>
        );
        id = item?.profileOrgId ?? "UNASSIGNED"; // Use profileOrgId as ID for assignees
      } else if (groupBy === "client") {
        title = (
          <>
          <Box display="flex" alignItems="center" gap={1}>
            <h1 className="accordion_title">
              {item?.name === "Not Client Tasks"
                ? "No Account Assigned"
                : normalizeLeadingCapital(item?.name)}
            </h1>
            <h3 className="accordion_title accordion_count">{item.totalCount}</h3>
          </Box>
          </>
          
        );
        id = item.orgId; // Use orgId as ID for clients
      } else if (groupBy === "interaction") {
        title = (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Tooltip title = {item.interactionTitle} placement = "top" arrow>
           <h1 className="accordion_title">
              {item.interactionTitle || "Independent Tasks"}
            </h1>
            </Tooltip>
            <h3 className="accordion_title accordion_count">{item.totalCount}</h3>
            {item.interactionStartTime && item.interactionTitle && (
              <h1 className="accordion__titl-date">
               ({formatDate(item.interactionStartTime)})
              </h1>
            )}
          </Box>
        );
        date = item.interactionStartTime
          ? `(${new Date(item.interactionStartTime).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })})`
          : "";
        id = item.interactionId; // Use interactionId as ID for interactions
      } else if (groupBy === "status") {
        title = (
          <>
          <Box display="flex" alignItems="center" gap={1}>
            <h1 className="accordion_title">
              {item.status === "in_progress" ? "In Progress" 
              : item.status === "to_do" ? "To Do"
              : item.status === "done" ? "Done"
              : item.status === "pr_review" ? "PR Review"
              : item.status === "product_testing" ? "Product Testing"
              : item.status === "devbox" ? "DevBox"
              : item.status === "parent_branch" ? "Parent Branch"
              : item.status === "dev_testing" ? "Dev Testing"
              : item.status === "dev_approved" ? "Dev Approved"
              : item.status === "test_approved" ? "Test Approved"
              : capitalizeFirstLetter(item.status)}
            </h1>
            <h3 className="accordion_title accordion_count">{item.totalCount}</h3>
          </Box>
          </>
        );
        id = item.status;
      } else if (groupBy === "recurring_interaction") {
        id = item.recurringEventId;
        title = (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Tooltip title={item.recurringInteractionTitle} placement="top" arrow>
              <h1 className="accordion_title">
                {item.recurringInteractionTitle}
              </h1>
            </Tooltip>
            <h3 className="accordion_title accordion_count">{item.totalTasksCount}</h3>
          </Box>
        );
        if (item.recurringEventId === "NON_RECURRING") {
          return {
            id,
            title,
            raw: item,
            body: renderActionItemsBody(),
          };
        }
        // CHILD ACCORDIONS: list of dates inside the recurring meeting
        const childAccordions =
        item.recurringInteractionSeries?.map((child: any) => ({
          id: String(child.interactionId),
          interactionId: child.interactionId,
          title: (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <h1 className="accordion_title">{formatDate(child.interactionStartTime)}</h1>
              <h3 className="accordion_title accordion_count">{child.totalCount}</h3>
            </Box>
          ),
          body: renderActionItemsBody(),
        })) || [];
        return {
          id,
          title,
          body: (
            <GroupByTask
              items={childAccordions}
              onAdd={(interactionId: any) => {
                // Pass the interactionId to parent “Add Task”
                setGroupByValues?.((prev) => ({
                  ...prev,
                  groupByInteractionId: interactionId,
                }));
                setIsNewActionItem?.(true);
                setIsPreview?.(false);
                setSelectedActionItem?.(null);
                setOpenDialog?.(true);
              }}
              onExpand={(interactionId) => {
                setActionItems([]);
                setGroupByValues?.({
                  groupByInteractionId: interactionId,
                  groupByClientDomain: null,
                  groupByAssigneeId: null,
                  groupByStatus : null,
                });
                if(interactionId) {
                  setGroupExpandToken((prev => prev + 1));
                }
                setSelectedGroup({
                  type : "recurring_interaction",
                  value: interactionId,
                })
              }}
              groupByRecurring = {groupBy === "recurring_interaction"}
              isCompleteMomTask ={ selectedTab === "completeMomTasks" ? true : false}
              isAiRecommendedTasks = {selectedTab === "aiRecommendedTasks" ? true : false}
            />
          ),
        };
      }

      return {
        title,
        date,
        id,
        raw: item,
        body: renderActionItemsBody(),
      };
    });
  };

  const closeTaskDialog = () => {
    setOpenDialog(false);
    setSelectedActionItem(null);
    setIsPreview(false);
    setIsNewActionItem(false);
    setIsActionItemValid(false);
    setIsSupportTicketOpen(false);
    setHasUnsavedChanges(false);

    setGroupByValues({
      groupByInteractionId: null,
      groupByClientDomain: { clientName: null, orgId: null },
      groupByAssigneeId: null,
      groupByStatus: null,
    });
  };

  const attemptCloseTaskDialog = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesConfirm(true);
    } else {
      closeTaskDialog();
    }
  };
  const accordionItems = generateAccordionItems();
  const isGroupedView = !!(groupBy && groupBy !== "undefined");
  return (
    <Box
      className="flex flex-col justify-start items-start w-full"
      sx={{
        ...(styles["meeting-container"] as any),
        overflow: "visible",
        position: "relative",
      }}
    >
      <Box
        className="flex justify-between items-center w-full h-auto"
        sx={{
          display: "flex",
          position: "relative",
          paddingRight: "200px",
          marginBottom: "20px",
          marginTop: 0,
        }}
      >
        {/* 🔹 LEFT SECTION — Filters */}
        <Box
          className="filters-left filter-scroll-container"
        >
          <Box></Box>
          <FilterPill
            key="meeting-date"
            selectedTab={selectedTab}
            type="date"
            label={selectedTab ==="aiRecommendedTasks" ? "Created Date": "Due Date"}
            value={tastManagementFilterDateRange}
            onChange={(val) => {
              // Clear old data and show loading immediately
              setActionItems([]);
              setTotalRecord(0);
              setPageNumber(1);
              setShowSpinner(true);
              
              if (val && typeof val === "object" && !Array.isArray(val) && "preset" in val){
                setTaskManagementFilterDateRange({
                  preset: val.preset,
                  startDate: val.startDate,
                  endDate: val.endDate,
                  overdue: !!val.overdue,
                });
                saveSessionDateRange({
                  preset: val.preset,
                  startDate: val.startDate,
                  endDate: val.endDate,
                  overdue: !!val.overdue,
                });

                if (!val.overdue) {
                  updateColumnFilter?.("dueDate", []);
                }
              } else if (typeof val === "string"){
                setTaskManagementFilterDateRange((prev) => {
                  const nextRange = {
                    ...prev,
                    preset: val || "",
                  };
                  saveSessionDateRange(nextRange);
                  return nextRange;
                });
              }
            }}
            parent_page="tasks-page"
            datePresetOptions={TASK_MANAGEMENT_DATE_PRESET_OPTIONS}
            showDateMenuHeader={selectedTab !== "aiRecommendedTasks"}
          />
          {filterOrder.map((key) => {
            if (selectedTab !== "aiRecommendedTasks" && key === "actions") {
              return null;
            }

            const value = columnFilters[key as keyof typeof columnFilters];
            if (!value || (Array.isArray(value) && value.length === 0)) {
              if (key !== "status") return null;
            }
            
            const pillColumns = ["account", "assignee", "coach", "sponsor", "priority", "status", "tags", "description", "subscribers", "actions"];
            if (!pillColumns.includes(key)) return null;

            let options: Options[] = [];
            let displayValue: string = "";
            let avatar: string | undefined;
            let email: string | undefined;

            switch (key) {
              case "assignee":
              case "coach":
              case "sponsor":
                options = getUniqueAssignees({ usersList: activeUsers }).map((option) => ({
                  ...option,
                  type: key as "assignee" | "coach" | "sponsor",
                  email: option.email,
                }));
                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                  const firstSelected = options.find((o) => o.value === value[0]);
                  avatar = firstSelected?.avatar;
                  email = firstSelected?.email;
                } else {
                  const selected = options.find((o) => o.value === value);
                  displayValue = selected?.label || (value as string);
                  avatar = selected?.avatar;
                  email = selected?.email;
                }
                break;
                case "description":
                  options = []; // No dropdown items, will use text box
                  displayValue = typeof value === "string" ? value : "";
                  break;
              case "account":
                options = getUniqueAccounts({ accountList: clientList }).map(option => ({
                  ...option,
                  type: "account",
                }));
                options.sort((a, b) => {
                  const aSelected = Array.isArray(value) ? value.includes(a.value) : a.value === value;
                  const bSelected = Array.isArray(value) ? value.includes(b.value) : b.value === value;
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;
                  return a.label.localeCompare(b.label);
                });
                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                } else {
                  displayValue =
                    options.find((o) => o.value === value)?.label || (value as string);
                }
                break;

              case "priority":
                options = PRIORITY_OPTIONS;
                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                } else {
                  displayValue =
                    options.find((o) => o.value === value)?.label || (value as string);
                }
                break;

              case "status":
                options = STATUS_OPTIONS;
                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                } else {
                  displayValue =
                    options.find((o) => o.value === value)?.label || (value as string);
                }
                break;

              case "tags":
                options = getUniqueTags({ tagsList });
                options.sort((a, b) => {
                  const aSelected = Array.isArray(value) ? value.includes(a.value) : a.value === value;
                  const bSelected = Array.isArray(value) ? value.includes(b.value) : b.value === value;
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;
                  return a.label.localeCompare(b.label);
                });
                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                } else {
                  displayValue =
                    options.find((o) => o.value === value)?.label || (value as string);
                }
                break;
              case "subscribers": {
                const filterType: Options["type"] = "subscribers";

                options = [
                  ...activeUsers.map<Options>((u) => ({
                    label: u.name,
                    value: u.profileOrgRelationshipId,
                    avatar: u.pictureUrl,
                    email: u.email,
                    entityType: "user",
                    type: filterType,
                  })),
                  ...spacesList.map<Options>((s) => ({
                    label: s.spaceName,
                    value: s.spaceId,
                    entityType: "space",
                    type: filterType,
                  })),
                ];

                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                  const firstSelected = options.find(
                    (o) => o.value === value[0].id && o.entityType === "user"
                  );
                  avatar = firstSelected?.avatar;
                  email = firstSelected?.email;
                }
                break;
              }

              case "actions": 
              options = ACTION_OPTIONS;
                if (Array.isArray(value)) {
                  displayValue = value
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(", ");
                } else {
                  displayValue =
                    options.find((o) => o.value === value)?.label || (value as string);
                }
                break;

              default:
                displayValue = Array.isArray(value) ? value.join(", ") : (value as string);
            }

            const getPillLabel = (key: string) => {
              switch (key) {
                case "assignee":
                  return "Solo Player";
                case "coach":
                  return "Solo Coach";
                default:
                  return capitalizeFirstLetter(key);
              }
            };

            return (
              <FilterPill
                key={key}
                label={capitalizeFirstLetter(key)}
                options={options}
                type={key === "description" ? "text" : "select"}
                value={
                  key === "description"
                    ? (value as string)
                    : key === "subscribers"
                    ? (Array.isArray(value) ? value.map(v => v.id) : [])
                    : Array.isArray(value)
                    ? value
                    : value
                    ? [value]
                    : []
                }
                displayLabel={getPillLabel(key)}
                displayValue={displayValue}
                avatar={avatar}
                onChange={(selectedValue) => {
                  if (key === "description") {
                    updateColumnFilter("description", selectedValue || "");
                    return;
                  }

                  if (key === "subscribers") {
                    if (!selectedValue || !Array.isArray(selectedValue)) {
                      updateColumnFilter("subscribers", []);
                      return;
                    }
                    const mapped = selectedValue
                      .map((val) => {
                        const option = options.find((o) => o.value === val);
                        return option
                          ? { id: option.value, entityType: option.entityType }
                          : null;
                      })
                      .filter(Boolean);
                    updateColumnFilter("subscribers", mapped);
                    return;
                  }

                  if (Array.isArray(selectedValue)) {
                    updateColumnFilter(key, selectedValue);
                  } else {
                    updateColumnFilter(key, selectedValue ? [selectedValue] : []);
                  }
                }}
                email={email}
                />
            );
          })}
        </Box>
        {/* 🔹 RIGHT SECTION — Actions */}
        <Box
          className="filters-right flex items-center gap-3"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "absolute",
            right: 0,
            top: 0,
          }}
        >
          <FilterPill
            key="groupBy-filter"
            selectedTab={selectedTab}
            type="select"
            label="Group By"
            options={[
              { label: "Interactions", value: "interaction" },
              { label: "Recurring Interactions", value: "recurring_interaction" },
              { label: "Account", value: "client" },
              { label: "Solo Player", value: "assignee" },
              { label: "Status", value: "status" }
            ]}
            value={groupBy}
            onChange={(val) => {
              const valueStr = val as string;
              setGroupBy(valueStr);
            }}
          />
            <ColumnFilter
            currentValue={tableColumnVisibility} // Pass the visibility state (Record<string, boolean>)
            onApply={(val) =>
              setColumnVisibility(
                resolveColumnVisibility(selectedTab, val as Record<string, boolean>)
              )
            }
            options={[
              { value: "serialnumber", label: "Serial No" },
              { value: "description", label: "Title", defaultValue: true },
              { value: "account", label: "Account" },
              { value: "assignee", label: "Solo Player", defaultValue: true },
              { value: "tags", label: "Tags" },
              { value: "coach", label: "Solo Coach" },
              { value: "createdAt", label: "Created At" },
              { value: "dueDate", label: "Due Date", defaultValue: true },
              { value: "priority", label: "Priority" },
              { value: "status", label: "Status" },
              { value: "momlink", label: "MoM Link" }, // Added MoM Link option
              { value: "sponsor", label: "Exec Sponsor" }, // Added Sponsor option
              { value: "subscribers", label: "Subscribers" },
              ...(selectedTab === "aiRecommendedTasks" ? [{ value: "actions", label: "Actions" }] : [])
            ]}
            type="toggle"
            showSections={{
              search: true,
              sort: false,
            }}
            parent_page="tasks_page"
          />
          { selectedTab === "aiRecommendedTasks" && exportAIRecommendedTasksDataEnabled && !groupBy && (
            <DownloadReport onDownload={handleExportAiRecommendedTasks} disabled={isAiRecommendedTasksExportPending} />
          )}
        </Box>
      </Box>
      <BulkUpdateBar
        visible={selectedActionItemIds.length > 0}
        onClose={() => {
          setSelectedActionItemIds([]);
          setBulkUpdateValues({}); // clear all selected fields
        }}
        onApply={handleActionItemUpdate}
        selectedActionItemIds= {selectedActionItemIds}
        selectedValues={bulkUpdateValues}
        setSelectedValues={setBulkUpdateValues}
        onDelete={()=>setCustomConfirm(true)}
        // onSubscribeToggle={handleSubscribeToggle}
        sections={[
          {
            key: "dueDate",
            label: "Due Date",
            icon: <img src={DueDateIcon} alt="date" style={{paddingBottom: "2px"}}/>,
            type: "date"
          },
          {
            key: "priority",
            label: "Priority",
            icon: <img src={DefaultFlag} alt="priority" />,
            type: "select",
            options: PRIORITY_OPTIONS,
            onSelect: (opt) => handleSelect("priority", opt),
          },
          {
            key: "status",
            label: "Status",
            icon: <img src={StatusIcon} alt="status" />,
            type: "select",
            options: STATUS_OPTIONS,
            onSelect: (opt) => handleSelect("status", opt),
          },
          {
            key: "account",
            label: "Account",
            icon: <img src= {AccountIcon} alt="Accounts" style={{paddingBottom: "2px"}} />,
            type: "search",
            options: accountList,
            onSelect: (opt) => handleSelect("account", opt)
          },
          {
            key: "soloPlayer",
            label: "Solo Player",
            icon:  <img src={AssigneeIcon} alt="Solo Player" style={{paddingBottom: "2px"}} />,
            type: "search",
            options: usersList,
            onSelect: (opt) => handleSelect("soloPlayer", opt),
            hideLabel: true,
            showAvatar: true,
          },
          {
            key: "soloCoach",
            label: "Solo Coach",
            icon:  <img src={SoloCoachIcon} alt="Solo Coach" style={{paddingBottom: "2px"}} />,
            type: "search",
            options: usersList,
            onSelect: (opt) => handleSelect("soloCoach", opt),
            hideLabel: true,
            showAvatar: true,
          },
          {
            key: "execSponser",
            label: "Exec Sponsor",
            icon:  <img src={ExecSponser} alt="Sponser" style={{paddingBottom: "2px"}} />,
            type: "search",
            options: usersList,
            onSelect: (opt) => handleSelect("execSponsor", opt),
            hideLabel: true,
            showAvatar: true,
          },
          {
            key: "subscribe",
            label: "Add Subscribers",
            icon:  <img src={SubscribeIcon} alt="subscribe" />,
            type: "search",
            options: [ ...usersList, ...spacesListFormatted],
            multi: true,
            hideLabel: true,
            showAvatar: true,
            onSelect: (selectedArray) => handleSelect("subscribe", selectedArray),
          },
          {
            key: "tags",
            label: "Tags",
            icon:  <img src={TagsListIcon} alt="tags" />,
            hideLabel: true,
            type: "custom",
            renderComponent: (
              <ActionItemsTagsCell
              value={
                bulkUpdateValues?.tags?.length
                  ? bulkUpdateValues.tags.map((t: { value: any; label: any; }) => ({
                      tagId: t.value,
                      tagName: t.label,
                    }))
                  : []
              }
                tagsList={formattedTagsList}
                canEdit={true}
                onChange={() =>{}}
                onForceChange={({ tagIds, tagNames }) => {
                  const formatted = tagIds.map((id, index) => ({
                    value: id,
                    label: tagNames[index],
                  }));
                
                  handleSelect("tags", formatted);
                }}
                noBorder={true}
                inlineSearch={true}
                inlineIconMode={true}
                hideDefaultIcon={false} 
                forceOpen= {true}
                row={undefined}
                saveButtonLabel="Add"
              />
            ),
          }          
        ]}
      />
          <SlideOver
          isOpen={openDialog}
          onClose={attemptCloseTaskDialog}
            showHeaderButtons={isNewActionItem ? true : false}
            onSave={handleSave}
            onDiscard={attemptCloseTaskDialog}
            discard={selectedActionItem != null ? false : true}
            saveDisabled={!isActionItemValid}
            closeOnRight
            >
        <ActionItem
          actionItemType={
            selectedTab === "teamTasks"
            ? "TEAM_ACTION_ITEMS"
            : selectedTab === "subscribedTasks"
            ? "SUBSCRIBED_ACTION_ITEMS"
            : "MY_ACTION_ITEMS"
          }
          attendees={attendees}
          selectedActionItem={selectedActionItem}
          handleClose={handleClose}
          isPreview={isPreview}
          selectedTab={selectedTab}
          startEndMeetingTime={startEndMeetingTime}
          saveTrigger={saveTrigger}
          discardTrigger={discardTrigger}
          resetSaveTrigger={() => setSaveTrigger(false)}
          resetDiscardTrigger={() => setDiscardTrigger(false)}
          openDialog={openDialog}
          onValidityChange={setIsActionItemValid}
          isNewActionItem={isNewActionItem}
          setOpenDialog={setOpenDialog}
          groupByValues= {groupByValues}
          tagsList={tagsList}
          spacesList={spacesList}
          meetingId={selectedActionItem?.interactionId}
          isSupportTask = {isSupportTicketOpen}
          onUnsavedChangesChange={setHasUnsavedChanges}
          />
      </SlideOver>
      {(selectedTab === "allTasks" ||
        selectedTab === "myTasks" ||
        selectedTab === "teamTasks" ||
        selectedTab === "subscribedTasks" || 
        selectedTab === "completeMomTasks" ||
        selectedTab === "aiRecommendedTasks") && (
        <Box
        ref={mainScrollRef}
          className="w-full "
          sx={{
            border: "1px solid #E5E5E5",
            borderRadius: "4px",
            padding: "0px",
            paddingTop: "0px",
            backgroundColor: "#FFFFFF",
            height: selectedActionItemIds.length > 0 ? "calc(100vh - 220px)" : groupBy && groupBy !== "undefined"? "calc(100vh - 160px)" : "calc(100vh - 155px)",
            maxHeight: selectedActionItemIds.length > 0 ? "calc(100vh - 220px)" : groupBy && groupBy !== "undefined"? "calc(100vh - 160px)" : "calc(100vh - 120px)",
            overflowY: isGroupedView ? "auto" : "hidden",
            overflowX: "hidden",
            scrollbarWidth: "thin",
          }}
          >
            {
              isGroupedView ? (
                <GroupByTask ref={groupByRef} items={accordionItems} loading = {isGroupbyLoadingState}
                onExpand={(id: string | null) => {
                  // Clear data immediately before setting new group to prevent flicker
                  const index = accordionItems.findIndex((i) => i.id === id);
                  expandedGroupIndexRef.current = index;
                  setActionItems([]);
                  setShowSpinner(true);
                  const clickedItem = accordionItems[index];
                  const isNotClientTasks = clickedItem?.raw?.name === "Not Client Tasks";
                  const isNonRecurringGroup = groupBy === "recurring_interaction" && id === "NON_RECURRING";
                  setIsNotClientTasks(isNotClientTasks);
                  setIsNonRecurringInteractionTasks(isNonRecurringGroup);
                  if (id || isNotClientTasks) {
                    setGroupExpandToken((prev) => prev + 1);
                  }
                  const isUnassigned = groupBy === "assignee" && id === "UNASSIGNED";
                  setIsUnassignedGroupExpanded(isUnassigned); 

                  setSelectedGroup({
                    type: groupBy,
                    value:
                      groupBy === "recurring_interaction"
                        ? ""
                        : isUnassigned
                        ? null
                        : id,
                  });
                }}
                setOpenDialog = {setOpenDialog}
                groupByValue ={groupBy}
                groupByList={groupByList}
                setGroupByValues={setGroupByValues}
                selectedGroupType={groupBy}
                setIsPreview = {setIsPreview}
                setSelectedActionItem ={setSelectedActionItem}
                setIsNewActionItem= {setIsNewActionItem}
                columnFilters={columnFilters} 
                dateRange ={tastManagementFilterDateRange}
                selectedTab={selectedTab}
                mainScrollRef={groupBy === "recurring_interaction" ? undefined : mainScrollRef}
                groupByRecurring = {groupBy !== "recurring_interaction"}
                isCompleteMomTask ={ selectedTab === "completeMomTasks" ? true : false}
                isAiRecommendedTasks = {selectedTab === "aiRecommendedTasks" ? true : false}
                />
              ) : (
          <ActionItems
            type={
              selectedTab === "teamTasks"
                ? "TEAM_ACTION_ITEMS"
                : selectedTab === "subscribedTasks"
                  ? "SUBSCRIBED_ACTION_ITEMS"
                  : "MY_ACTION_ITEMS"
            }
            isLoading={isLoadingForTable}
            actionItems={actionItems}
            totalRecord={totalRecords}
            handleActionItemSelection={handleActionItemSelection}
            handleActionItemPreview={handleActionItemPreview}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onFilter={onFilter}
            actionItemType={actionItemType}
            onTypeChange={onTypeChange}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            startEndMeetingTime={startEndMeetingTime}
            selectedTab={selectedTab}
            handleEditIconClick={handleEditIconClick}
            showSpinner = {showSpinner}
            setShowSpinner= {setShowSpinner}
            columnFilters = {columnFilters}
            updateColumnFilter ={updateColumnFilter}
            sort={sort} // New: Pass sort state
            onSortChange={(columnKey: string, direction: "asc" | "desc") => {
              setSort({
                sort_field: columnKey,
                sort_order: direction,
              });
            }}
            activeUsers={activeUsers}
            clientList={clientList}
            setSelectedActionItemIds={setSelectedActionItemIds}
            selectedActionItemIds={selectedActionItemIds}
            bulkUpdateValues={bulkUpdateValues}
            tagsList={tagsList}
            groupByValues= {groupByValues}
            showAddTask={selectedTab === "completeMomTasks" || selectedTab === "aiRecommendedTasks" ? false :true}
            spacesList={spacesList}
            groupBy = {groupBy}
            columnVisibility={tableColumnVisibility}
            openTaskDialog={openDialog}
            tableMaxHeight= {selectedActionItemIds.length > 0 ? "calc(100vh - 300px)" :"calc(100vh - 245px)"}
            tableMinHeight={selectedActionItemIds.length > 0 ? "calc(100vh - 300px)" :"calc(100vh - 230px)"}
            isCountLoading={isCountLoading}
          />
              )}
        </Box>
      )}
      <CustomConfirmation isOpen={customConfirm} 
      title={`Do you want to delete the selected task${
        selectedActionItemIds.length > 1 ? "s" : ""
      }?`}
      handleSuccess={()=>{
        mutateActionItemDelete({ actionItemId: getRealSelectedActionItemIds(selectedActionItemIds) });
      }}
       handleClose={()=> setCustomConfirm(false)}
       width = "auto"
       isLoading = {showSpinner}
       />
        <CustomConfirmation
        isOpen={showUnsavedChangesConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard or save them?"
        width="380px"
        actionBtnLabel="Save"
        cancelBtnLabel="Discard"
        handleSuccess={() => {
          // SAVE
          setShowUnsavedChangesConfirm(false);
          setSaveTrigger(true);
        }}
        handleClose={() => {
          // DISCARD
          setShowUnsavedChangesConfirm(false);
          setDiscardTrigger(true);
          closeTaskDialog();
        }}
        handleCancel={() => {
          // CANCEL - just close the confirmation and keep the dialog open
          setShowUnsavedChangesConfirm(false);
          setDiscardTrigger(false);
        }}
        backgroundcolor
        actionType = "save"
      />
    </Box>
  );
})

export default TaskManagement;
